import React, {useState} from "react";
import {useObserver} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {Table, Space, Popconfirm, Row, Col, Select, Tooltip, Input, Grid} from "antd";
import {PlusOutlined, EditOutlined, DeleteOutlined} from "@ant-design/icons";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import {useFetch} from "../../../hooks/useFetch";
import stores from "../../../store/stores";
import {CreateZoneModal} from "../modals/CreateZoneModal";
import {UpdateZoneModal} from "../modals/UpdateZoneModal";
import {Link} from "react-router-dom";
import {arrayAlphabeticalSort} from "utils/helpers";
import {toTurkishProperCase} from "utils/toTurkishProperCase";
import {CityFilter} from "components/_defaultComponents/FilterComponents";

const Zone = ({isActive}) => {
    const [recallZone, setRecallZone] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);
    const [zoneManagementFilter, setZoneManagementFilter] = useState(null);
    const [cityFilter, setCityFilter] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {t} = useTranslation(["common"]);
    const isMobile = window.innerWidth <= 768;

    // Zone Management listesi
    const [zoneManagementLoading, zoneManagementData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getZoneManagementList.bind(null, {
            skip: 0,
            limit: 1000,
            paginate: false
        }),
        condition: isActive,
        dependencies: [isActive]
    });

    // Zone Hook
    const [zoneLoading, zoneData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getZoneList.bind(null, {
            skip: (currentPage - 1) * pageSize,
            limit: pageSize,
            paginate: true,
            zoneManagementId: zoneManagementFilter,
            cityName: cityFilter,
            searchText: searchText
        }),
        condition: isActive,
        dependencies: [
            recallZone,
            zoneManagementFilter,
            cityFilter,
            searchText,
            isActive,
            currentPage,
            pageSize
        ],
        waitForResponse: true
    });

    // Zone İşlemleri
    const handleCreateZone = () => {
        setCreateModalVisible(true);
    };

    const handleEditZone = record => {
        setSelectedZone(record);
        setEditModalVisible(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalVisible(false);
    };

    const handleCloseEditModal = () => {
        setEditModalVisible(false);
        setSelectedZone(null);
    };

    const handleDeleteZone = async record => {
        const result = await stores.operationOrganizationStore.deleteZone({
            id: record._id
        });
        if (result) {
            setRecallZone(!recallZone);
        }
    };

    const handleZoneManagementFilterChange = value => {
        setZoneManagementFilter(value);
        setCurrentPage(1); // Filtre değiştiğinde sayfa 1'e reset
    };

    const handleCityFilterChange = value => {
        setCityFilter(value);
        setCurrentPage(1); // Filtre değiştiğinde sayfa 1'e reset
    };

    const clearFilters = () => {
        setZoneManagementFilter(null);
        setCityFilter(null);
        setSearchText(null);
        setCurrentPage(1); // Filtreler temizlendiğinde sayfa 1'e reset
    };

    // Table pagination change handler
    const handleTableChange = pagination => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    return useObserver(() => (
        <React.Fragment>
            <CreateZoneModal
                visibility={createModalVisible}
                onCancel={handleCloseCreateModal}
                setRecallZone={setRecallZone}
            />

            <UpdateZoneModal
                visibility={editModalVisible}
                detail={selectedZone}
                onCancel={handleCloseEditModal}
                setRecallZone={setRecallZone}
            />

            <Row gutter={[8, 8]} justify="space-between" style={{marginBottom: 16}}>
                <Col xs={24} md={18}>
                    <Space wrap style={{width: "100%"}}>
                        <Input
                            placeholder="Bölge Adı"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{width: isMobile ? "100%" : 200}}
                        />
                        <Select
                            placeholder="Bölge Müdürlüğü Seç"
                            style={{width: isMobile ? "100%" : 200}}
                            allowClear
                            value={zoneManagementFilter}
                            onChange={handleZoneManagementFilterChange}
                            loading={zoneManagementLoading}
                        >
                            {Array.isArray(zoneManagementData?.data) &&
                                arrayAlphabeticalSort(zoneManagementData?.data || [], "name").map(
                                    item => (
                                        <Select.Option key={item._id} value={item._id}>
                                            {item.name}
                                        </Select.Option>
                                    )
                                )}
                        </Select>
                        <div style={{width: isMobile ? "100%" : 200}}>
                            <CityFilter
                                value={cityFilter}
                                onChange={handleCityFilterChange}
                                onClear={handleCityFilterChange}
                                allowClear
                                showLabel={false}
                                selectMarginBottom="0px"
                                placeholder="Şehir Seç"
                            />
                        </div>

                        <CustomButton
                            onClick={clearFilters}
                            style={{width: isMobile ? "100%" : undefined}}
                        >
                            Filtreleri Temizle
                        </CustomButton>
                    </Space>
                </Col>
                <Col xs={24} md={6} style={{textAlign: isMobile ? "left" : "right"}}>
                    <CustomButton
                        onClick={handleCreateZone}
                        icon={<PlusOutlined />}
                        style={{width: isMobile ? "100%" : undefined}}
                    >
                        Yeni Bölge
                    </CustomButton>
                </Col>
            </Row>

            <Table
                scroll={{x: "max-content"}}
                size="small"
                rowKey="_id"
                loading={zoneLoading}
                dataSource={Array.isArray(zoneData?.data) ? zoneData.data : []}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                onChange={handleTableChange}
                pagination={{
                    hideOnSinglePage: true,
                    current: currentPage,
                    pageSize: pageSize,
                    total: zoneData?.dataCount || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
                    pageSizeOptions: ["10", "20", "50", "100"]
                }}
            >
                <Table.Column
                    dataIndex="name"
                    title="Bölge Adı"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={150}
                    render={name => (
                        <Tooltip title={toTurkishProperCase(name)}>
                            <b>{name}</b>
                        </Tooltip>
                    )}
                />

                <Table.Column
                    dataIndex="zoneManagementId"
                    title="Bölge Müdürlüğü"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={150}
                    render={zoneManagement => (
                        <Tooltip title={toTurkishProperCase(zoneManagement?.name)}>
                            <span>{zoneManagement?.name || "-"}</span>
                        </Tooltip>
                    )}
                />
                <Table.Column
                    dataIndex="zoneManagementId"
                    title="Bölge Müdürü"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={200}
                    render={zoneManagement => {
                        const bmUser = zoneManagementData?.data?.find(
                            item => item?._id?.toString() === zoneManagement?._id?.toString()
                        )?.bmUser;

                        if (!bmUser) return "-";

                        return (
                            <Tooltip
                                title={toTurkishProperCase(bmUser?.fullName || bmUser?.username)}
                            >
                                <Link
                                    target="_blank"
                                    to={`/users/${bmUser._id}`}
                                    style={{color: "#1890ff"}}
                                >
                                    {bmUser.fullName || bmUser.username}
                                </Link>
                            </Tooltip>
                        );
                    }}
                />

                <Table.Column
                    dataIndex="cityName"
                    title="Şehir"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={100}
                    render={cityName => (
                        <Tooltip title={toTurkishProperCase(cityName)}>
                            <span>{toTurkishProperCase(cityName) || "-"}</span>
                        </Tooltip>
                    )}
                />

                <Table.Column
                    dataIndex="districtNames"
                    title="İlçeler"
                    width={250}
                    ellipsis={{
                        tooltip: true
                    }}
                    style={{
                        whiteSpace: "normal"
                    }}
                    render={districtNames => (
                        <Tooltip
                            title={districtNames
                                ?.map(districtName => toTurkishProperCase(districtName))
                                ?.join(", ")}
                        >
                            {Array.isArray(districtNames) && districtNames?.length > 0 ? (
                                (() => {
                                    const text = districtNames
                                        .map(districtName => toTurkishProperCase(districtName))
                                        .join(", ");
                                    return text.length > 20 ? text.slice(0, 20) + "..." : text;
                                })()
                            ) : (
                                <span>-</span>
                            )}
                        </Tooltip>
                    )}
                />

                <Table.Column
                    dataIndex="syUsers"
                    title="Saha Yöneticisi"
                    ellipsis={{
                        tooltip: true
                    }}
                    render={syUsers => {
                        if (!syUsers || syUsers?.length === 0) return "-";

                        return (
                            <>
                                <Tooltip
                                    title={
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4
                                            }}
                                        >
                                            {syUsers?.map(syUser => (
                                                <span key={syUser._id}>
                                                    <span>- </span>
                                                    <Link
                                                        target="_blank"
                                                        to={`/users/${syUser._id}`}
                                                        style={{color: "#1890ff"}}
                                                    >
                                                        {toTurkishProperCase(
                                                            syUser?.fullName || syUser?.username
                                                        )}
                                                    </Link>
                                                </span>
                                            ))}
                                        </div>
                                    }
                                >
                                    {(() => {
                                        const names =
                                            syUsers
                                                ?.map(syUser =>
                                                    toTurkishProperCase(
                                                        syUser?.fullName || syUser?.username
                                                    )
                                                )
                                                ?.join(", ") || "-";
                                        return names.length > 20
                                            ? names.slice(0, 20) + "..."
                                            : names;
                                    })()}
                                </Tooltip>
                            </>
                        );
                    }}
                />
                <Table.Column
                    dataIndex="ikUsers"
                    title="İk"
                    ellipsis={{
                        tooltip: true
                    }}
                    render={ikUsers => {
                        if (!ikUsers || ikUsers?.length === 0) return "-";

                        return (
                            <>
                                <Tooltip
                                    title={
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4
                                            }}
                                        >
                                            {ikUsers?.map(ikUser => (
                                                <span key={ikUser._id}>
                                                    <span>- </span>
                                                    <Link
                                                        target="_blank"
                                                        to={`/users/${ikUser._id}`}
                                                        style={{color: "#1890ff"}}
                                                    >
                                                        {toTurkishProperCase(
                                                            ikUser?.fullName || ikUser?.username
                                                        )}
                                                    </Link>
                                                </span>
                                            ))}
                                        </div>
                                    }
                                >
                                    {(() => {
                                        const names =
                                            ikUsers
                                                ?.map(ikUser =>
                                                    toTurkishProperCase(
                                                        ikUser?.fullName || ikUser?.username
                                                    )
                                                )
                                                ?.join(", ") || "-";
                                        return names.length > 20
                                            ? names.slice(0, 20) + "..."
                                            : names;
                                    })()}
                                </Tooltip>
                            </>
                        );
                    }}
                />
                <Table.Column
                    dataIndex="agencyUsers"
                    title="Acente"
                    ellipsis={{
                        tooltip: true
                    }}
                    render={agencyUsers => {
                        if (!agencyUsers || agencyUsers?.length === 0) return "-";

                        return (
                            <>
                                <Tooltip
                                    title={
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4
                                            }}
                                        >
                                            {agencyUsers?.map(agencyUser => (
                                                <span key={agencyUser._id}>
                                                    <span>- </span>
                                                    <Link
                                                        target="_blank"
                                                        to={`/users/${agencyUser._id}`}
                                                        style={{color: "#1890ff"}}
                                                    >
                                                        {toTurkishProperCase(
                                                            agencyUser?.fullName ||
                                                                agencyUser?.username
                                                        )}
                                                    </Link>
                                                </span>
                                            ))}
                                        </div>
                                    }
                                >
                                    {(() => {
                                        const names =
                                            agencyUsers
                                                ?.map(agencyUser =>
                                                    toTurkishProperCase(
                                                        agencyUser?.fullName || agencyUser?.username
                                                    )
                                                )
                                                ?.join(", ") || "-";
                                        return names.length > 20
                                            ? names.slice(0, 20) + "..."
                                            : names;
                                    })()}
                                </Tooltip>
                            </>
                        );
                    }}
                />

                <Table.Column
                    dataIndex="agencyIkUsers"
                    title="Acente-İK"
                    ellipsis={{
                        tooltip: true
                    }}
                    render={agencyIkUsers => {
                        if (!agencyIkUsers || agencyIkUsers?.length === 0) return "-";

                        return (
                            <>
                                <Tooltip
                                    title={
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4
                                            }}
                                        >
                                            {agencyIkUsers?.map(agencyIkUser => (
                                                <span key={agencyIkUser._id}>
                                                    <span>- </span>
                                                    <Link
                                                        target="_blank"
                                                        to={`/users/${agencyIkUser._id}`}
                                                        style={{color: "#1890ff"}}
                                                    >
                                                        {toTurkishProperCase(
                                                            agencyIkUser?.fullName ||
                                                                agencyIkUser?.username
                                                        )}
                                                    </Link>
                                                </span>
                                            ))}
                                        </div>
                                    }
                                >
                                    {(() => {
                                        const names =
                                            agencyIkUsers
                                                ?.map(agencyIkUser =>
                                                    toTurkishProperCase(
                                                        agencyIkUser?.fullName ||
                                                            agencyIkUser?.username
                                                    )
                                                )
                                                ?.join(", ") || "-";
                                        return names.length > 20
                                            ? names.slice(0, 20) + "..."
                                            : names;
                                    })()}
                                </Tooltip>
                            </>
                        );
                    }}
                />

                <Table.Column
                    align="right"
                    title={t("actions.action", {ns: "common"})}
                    render={item => (
                        <Space>
                            <CustomButton
                                size="small"
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => handleEditZone(item)}
                            >
                                Düzenle
                            </CustomButton>
                            <Popconfirm
                                title="Bu bölgeyi silmek istediğinizden emin misiniz?"
                                onConfirm={() => handleDeleteZone(item)}
                                okText="Evet"
                                cancelText="Hayır"
                            >
                                <CustomButton
                                    size="small"
                                    type="link"
                                    danger
                                    icon={<DeleteOutlined />}
                                >
                                    Sil
                                </CustomButton>
                            </Popconfirm>
                        </Space>
                    )}
                />
            </Table>
        </React.Fragment>
    ));
};

export default Zone;
